import type { BillingProvider, SeatType as ProductSeatType } from '@/api/generated/api'

export type Role = 'admin' | 'member' | 'superadmin' | 'it_admin'
export type InviteStatus =
  'pending' | 'accepted' | 'expired' | 'error' | 'removed' | 'revoked' | 'admin_removed' | 'scim_removed'
export type EligibleTeamUser =
  'orphan' | 'eligible-org-with-members' | 'eligible-org-no-members' | 'eligible-independent' | 'inactive'
export type UserTeamsStatus = Role | EligibleTeamUser

export enum SubscriptionStatus {
  Active = 'active',
  Inactive = 'inactive',
  PastDue = 'past_due',
  Unpaid = 'unpaid',
  Canceled = 'canceled',
  Incomplete = 'incomplete',
  IncompleteExpired = 'incomplete_expired',
  Trialing = 'trialing',
}

export const ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES: string[] = [
  SubscriptionStatus.Active,
  SubscriptionStatus.Trialing,
  SubscriptionStatus.PastDue,
]

// Absent/null means the backend could not classify the org; treat it as not-Business.
export type BillingTier = 'business' | 'enterprise' | null

export interface EnterpriseSubscription {
  stripePriceId: string
  productName: string
  // we should change this to SubscriptionStatus, leaving this as todo for a different refactor work
  status: string
  numSeats: number
  seatsCurrentCycle: number
  planEndsAt: number
  cancelAt: number | null
  cancelAtPeriodEnd: boolean | null
  tier?: BillingTier
  billingProvider?: BillingProvider
}

export enum SettingType {
  zdr = 'zdr',
  allowShareData = 'allowShareData',
  allowCloudSync = 'allowCloudSync',
  zdrLocked = 'zdrLocked',
  localDataDelete = 'localDataDelete',
  enforceSSO = 'enforceSSO',
  autoInvite = 'autoInvite',
  disableTeamTrial = 'disableTeamTrial',
  usageExportAllowed = 'usageExportAllowed',
  showLastUsedAt = 'showLastUsedAt',
  restrictDomainAccess = 'restrictDomainAccess',
  contextAwareness = 'contextAwareness',
  hideLeaderboard = 'hideLeaderboard',
  allowNoteSharing = 'allowNoteSharing',
  allowNotetaker = 'allowNotetaker',
  allowScratchpad = 'allowScratchpad',
  allowDictionary = 'allowDictionary',
  allowSnippets = 'allowSnippets',
  ipAllowlist = 'ipAllowlist',
  ipAllowlistLocked = 'ipAllowlistLocked',
  ipAllowlistUiVisible = 'ipAllowlistUiVisible',
  granularDataControlsUiVisible = 'granularDataControlsUiVisible',
  appDenyList = 'appDenyList',
  appDenyListUiVisible = 'appDenyListUiVisible',
  browserUrlDenyList = 'browserUrlDenyList',
  notetakerBlockedDomains = 'notetakerBlockedDomains',
  notetakerBlockedDomainsUiVisible = 'notetakerBlockedDomainsUiVisible',
  notetakerTranscriptRetention = 'notetakerTranscriptRetention',
  notetakerConsentConfirmation = 'notetakerConsentConfirmation',
  notetakerConsentChatEnabled = 'notetakerConsentChatEnabled',
  notetakerConsentChatMessage = 'notetakerConsentChatMessage',
  snippetSharingPolicy = 'snippetSharingPolicy',
  mcpEnabled = 'mcpEnabled',
  allowTransforms = 'allowTransforms',
  connectorGoogleCalendarEnabled = 'connectorGoogleCalendarEnabled',
  connectorGmailEnabled = 'connectorGmailEnabled',
  connectorSlackEnabled = 'connectorSlackEnabled',
  connectorNotionEnabled = 'connectorNotionEnabled',
  connectorOutlookEnabled = 'connectorOutlookEnabled',
  auditLogStreamingUiVisible = 'auditLogStreamingUiVisible',
  dictionarySnippetsKillSwitchUiVisible = 'dictionarySnippetsKillSwitchUiVisible',
  noteSharingAudience = 'noteSharingAudience',
  noteSharingContent = 'noteSharingContent',
  orgInsightsMode = 'orgInsightsMode',
  mdmProviders = 'mdmProviders',
  mdmUpdateIntent = 'mdmUpdateIntent',
  enforceMuteAudio = 'enforceMuteAudio',
  defaultSeatType = 'defaultSeatType',
  disableMobileSignIn = 'disableMobileSignIn',
}

export enum LocalDataPolicy {
  StoreNormally = 'store_normally',
  DeleteAfter24h = 'delete_after_24h',
  NeverStore = 'never_store',
  // Org-only: locks every member to Store. Members' own preference never takes this value.
  AlwaysStore = 'always_store',
}

export enum ContextAwarenessPolicy {
  Available = 'available',
  Disabled = 'disabled',
}

// Governs which sharing-scope options non-admin members see when creating a snippet.
// Admins always bypass this policy. Values are hand-written to match the backend's
// EnterpriseSettings value validator (aria-web has no OpenAPI-generated enum for this).
export enum SnippetSharingPolicy {
  Unrestricted = 'unrestricted',
  DepartmentOnly = 'department_only',
}

// Hand-written to match the backend validator — aria-web exposes no generated enum for this.
// Declaration order is the tier ladder, least to most permissive.
export enum NoteSharingAudience {
  Owner = 'owner',
  Internal = 'internal',
  External = 'external',
  Public = 'public',
}

// Hand-written to match the backend validator. Orthogonal to NoteSharingAudience: the audience
// picks who may receive a note, this picks how much of it they get.
export enum NoteSharingContent {
  NotesOnly = 'notes_only',
  NotesAndTranscript = 'notes_and_transcript',
}

// Hand-written to match the backend validator. Sets what a member's new meeting starts as for
// org insights; members can switch any meeting unless the org picks Disabled.
export enum OrgInsightsMode {
  DefaultOn = 'default_on',
  DefaultOff = 'default_off',
  Individual = 'individual',
  Disabled = 'disabled',
}

// Hand-written to match the backend validator. What the admin says they expect from MDM-managed
// updates; it is a label for support, not a control.
export enum MdmUpdateIntent {
  Auto = 'auto',
  Manual = 'manual',
}

// Hand-written to match the backend validator. An MDM tool the admin says they use.
export enum MdmProvider {
  Intune = 'intune',
  Jamf = 'jamf',
  Kandji = 'kandji',
  Mosyle = 'mosyle',
  WorkspaceOne = 'workspace_one',
  Jumpcloud = 'jumpcloud',
  Rippling = 'rippling',
  Fleet = 'fleet',
  Munki = 'munki',
  Other = 'other',
}

// Org-level Notetaker transcript retention policy. Superset of the generated
// `NotetakerTranscriptRetention` (the per-user desktop enum) — the org adds
// `ManagedIndividually` (defer the choice to each user) on top of the windows.
// Hand-written because enterprise settings are not OpenAPI-generated.
export enum NotetakerTranscriptRetention {
  ManagedIndividually = 'managed_individually',
  Days1 = '1_day',
  Days7 = '7_days',
  Days30 = '30_days',
  Days90 = '90_days',
  Days180 = '180_days',
  Days365 = '365_days',
  NeverDelete = 'never_delete',
}

// Per-domain capture policy, stored on each enterprise_domain row (no longer a
// team-wide enterprise setting). ``unset`` is the only value allowed while the
// domain is still unverified.
// Re-exported from the generated OpenAPI client so we have a single source of
// truth for the enum values. The generated form is a `{ ... } as const` + union
// type; property access (``DomainCapturePolicy.Require``) is identical to the
// hand-written enum we used to keep here.
export { DomainCapturePolicy } from '@/api/generated/api'

// Product allocation for a member (`dictation` / `bundle` / `no-access`). Aliased because
// `@/api/enterprise` already exports an unrelated billing-status `SeatType`.
export { SeatType as ProductSeatType } from '@/api/generated/api'

interface AppDenyEntryBase {
  displayName: string
}

export type AppDenyEntry =
  | (AppDenyEntryBase & { macBundleId: string; winProcessName?: string | null })
  | (AppDenyEntryBase & { macBundleId?: string | null; winProcessName: string })

/**
 * One canonical Notetaker domain rule as the server stores it. Admins type free
 * text (`*@acme.com`, `acme.com`, `bob@acme.com`) and the server is the only
 * classifier, so `kind`/`value` are read-only here — never derived client-side.
 */
export interface NotetakerBlockedDomain {
  kind: 'domain' | 'email'
  value: string
  policy: 'blocked'
}

export interface EnterpriseSettings {
  [SettingType.zdr]?: boolean
  [SettingType.allowShareData]?: boolean
  [SettingType.allowCloudSync]?: boolean
  [SettingType.zdrLocked]?: boolean
  [SettingType.localDataDelete]?: LocalDataPolicy
  [SettingType.enforceSSO]?: boolean
  [SettingType.autoInvite]?: boolean
  [SettingType.disableTeamTrial]?: boolean
  [SettingType.usageExportAllowed]?: boolean
  [SettingType.showLastUsedAt]?: boolean
  [SettingType.restrictDomainAccess]?: boolean
  [SettingType.contextAwareness]?: ContextAwarenessPolicy
  [SettingType.hideLeaderboard]?: boolean
  [SettingType.enforceMuteAudio]?: boolean
  [SettingType.ipAllowlist]?: string[] | null
  [SettingType.ipAllowlistLocked]?: boolean
  [SettingType.ipAllowlistUiVisible]?: boolean
  [SettingType.granularDataControlsUiVisible]?: boolean
  [SettingType.appDenyList]?: AppDenyEntry[] | null
  [SettingType.appDenyListUiVisible]?: boolean
  // Flat list of canonical hosts (e.g. `chatgpt.com`) blocked in browsers.
  // Shares the appDenyListUiVisible gate. Empty/absent = nothing blocked.
  [SettingType.browserUrlDenyList]?: string[] | null
  [SettingType.allowNoteSharing]?: boolean
  [SettingType.allowNotetaker]?: boolean
  [SettingType.allowScratchpad]?: boolean
  // Absent = allowed. False blocks members from adding/editing; saved entries keep applying.
  [SettingType.allowDictionary]?: boolean
  [SettingType.allowSnippets]?: boolean
  // Canonical rules as stored. PATCHed as a list of free-text strings — the server
  // canonicalizes and returns these objects. Empty/absent = no meeting is blocked.
  [SettingType.notetakerBlockedDomains]?: NotetakerBlockedDomain[] | null
  [SettingType.notetakerBlockedDomainsUiVisible]?: boolean
  // Server-managed; stamp presence = Notetaker blocked. Not a SettingType PATCH key.
  notetakerDisabledAt?: string
  // Server-managed; stamp presence = early access enabled ahead of the Sept 30 GA. Not a SettingType PATCH key.
  notetakerBusinessEarlyAccessAt?: string
  [SettingType.notetakerTranscriptRetention]?: NotetakerTranscriptRetention
  [SettingType.notetakerConsentConfirmation]?: boolean
  [SettingType.notetakerConsentChatEnabled]?: boolean
  // Admin-authored consent notice. Absent/null hands the copy back to the desktop client's
  // own localized default, so the box is prefilled with DEFAULT_CONSENT_CHAT_MESSAGE.
  [SettingType.notetakerConsentChatMessage]?: string | null
  [SettingType.snippetSharingPolicy]?: SnippetSharingPolicy
  [SettingType.mcpEnabled]?: boolean
  [SettingType.allowTransforms]?: boolean
  [SettingType.connectorGoogleCalendarEnabled]?: boolean
  [SettingType.connectorGmailEnabled]?: boolean
  [SettingType.connectorSlackEnabled]?: boolean
  [SettingType.connectorNotionEnabled]?: boolean
  [SettingType.connectorOutlookEnabled]?: boolean
  [SettingType.auditLogStreamingUiVisible]?: boolean
  // On unless a Wispr admin turns it off: whether the org may use the Dictionary/Snippets switches.
  [SettingType.dictionarySnippetsKillSwitchUiVisible]?: boolean
  [SettingType.noteSharingAudience]?: NoteSharingAudience
  [SettingType.noteSharingContent]?: NoteSharingContent
  [SettingType.orgInsightsMode]?: OrgInsightsMode
  // Absent until an admin completes the MDM setup step.
  [SettingType.mdmProviders]?: MdmProvider[]
  [SettingType.mdmUpdateIntent]?: MdmUpdateIntent
  // Absent = the server derives a default from the plan; never write one implicitly.
  [SettingType.defaultSeatType]?: ProductSeatType
  // Blocks new iOS/Android sign-ins only; existing mobile sessions are not revoked.
  [SettingType.disableMobileSignIn]?: boolean
}

export type EnterpriseGates = Pick<
  EnterpriseSettings,
  | SettingType.usageExportAllowed
  | SettingType.showLastUsedAt
  | SettingType.zdrLocked
  | SettingType.restrictDomainAccess
  | SettingType.ipAllowlistLocked
  | SettingType.ipAllowlistUiVisible
  | SettingType.granularDataControlsUiVisible
  | SettingType.appDenyListUiVisible
  | SettingType.auditLogStreamingUiVisible
  | SettingType.dictionarySnippetsKillSwitchUiVisible
  | SettingType.notetakerBlockedDomainsUiVisible
>

export type GateKey = keyof EnterpriseGates

export interface Enterprise {
  id: string
  name: string
  domain: string | null
  domains: string[]
  logo: string | null
  workosProvider: string | null
  createdAt: string
  userRole: Role
  subscription: EnterpriseSubscription | null
  settings: EnterpriseSettings
  hasNotetakerEntitlementOverrides?: boolean
  // Server gate for choosing a seat type; absent from older backends, which means disabled.
  seatSelectionEnabled?: boolean
  // Server kill switch for the mobile sign-in block; absent from older backends, which means hidden.
  mobileSignInBlockAvailable?: boolean
  hipaaBaaSigned: boolean
  hipaaBaaSignedBy?: string
  hipaaBaaSignedByEmail?: string
  hipaaBaaSignedAt?: string
  scimEnabled: boolean
  preserveContractedSeats: boolean
  costCenterBillingEnabled?: boolean
  team_count?: number
}

export enum TeamDomainStatus {
  Available = 'available',
  Exists = 'exists',
  Forbidden = 'forbidden',
  Error = 'error',
}

export interface Member {
  userId: string
  firstName: string
  lastName: string
  avatarUrl: string
  role: Role
  email: string
  enterpriseId: string
  teamTrialGiven: boolean
  trialEndsAt: number | null
  hasIndividualSubscription?: boolean | null
  /** Sent to admins only; null when no seat is recorded. */
  seatType?: ProductSeatType | null
  /** Sent to admins only; where a seat change for this member goes, null when not on a team. */
  costCenterId?: string | null
}

export interface Invite {
  id: string
  email: string
  status: InviteStatus
  expiresAt: string
  lastSentAt?: string | null
  role: Role
  intended_seat_type?: ProductSeatType | null
}

export interface TeamInviteLinkResponse {
  code: string
  invite_url: string
  enterprise_id: string
}

export enum PaymentSchedule {
  Monthly = 'Monthly',
  Annual = 'Annual',
}

export enum PlanType {
  Pro = 'Pro',
  Team = 'Teams',
  Enterprise = 'Enterprise',
}
